import React from 'react';

const ComputeClusterSection: React.FC = () => {
  return (
    <section className="relative py-20 bg-gradient-to-br from-sky-50 via-slate-50 to-emerald-50">
      {/* 和上一个 section 之间的分隔线 */}
      <div className="pointer-events-none absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent" />

      <div className="max-w-6xl mx-auto px-6">
        {/* 上半部分：标题 + 描述 + 右侧“示意图” */}
        <div className="grid gap-10 lg:grid-cols-[minmax(0,2fr)_minmax(0,1.5fr)] items-center mb-12">
          {/* 文案区 */}
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              算力与计算平台
            </h2>
            <p className="text-sm md:text-base text-gray-700 mb-4 max-w-3xl">
              我们已搭建面向 AI 与多组学分析的高性能计算平台，包含多节点、多规格的 GPU
              服务器集群，支撑从方法学探索到生产级部署的完整算力需求。
              下表展示的是其中两类
              <span className="font-semibold text-emerald-700">
                代表性生产集群配置
              </span>
              ，用于帮助合作伙伴快速了解平台的基础算力水位。
            </p>

            <ul className="space-y-2 text-sm md:text-base text-gray-700">
              <li className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-emerald-500" />
                <span>支持大模型训练、单细胞 / 多组学分析与复杂深度学习建模等高负载任务</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-sky-500" />
                <span>单节点内存最高 1 TB，显存可达 320 GB 以上，适配百亿参数量级模型</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-violet-500" />
                <span>支持多任务并行、模型原型验证与生产部署，便于快速落地科研与应用项目</span>
              </li>
            </ul>
          </div>

          {/* 右侧：简洁“图片感”算力卡片 */}
          <div className="hidden lg:block">
            <div className="relative w-full max-w-md ml-auto">
              {/* 小标签 */}
              <div className="absolute -top-3 left-6 inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1 text-xs font-medium text-emerald-700 shadow-sm border border-emerald-100">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                GPU 计算平台
              </div>

              <div className="rounded-3xl bg-white shadow-xl border border-slate-200 p-6">
                {/* 顶部图形示意：抽象机柜 / GPU 卡 */}
                <div className="mb-5 flex items-center gap-4">
                  <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-400 via-blue-500 to-emerald-400 flex items-center justify-center shadow-md">
                    <div className="w-10 h-6 rounded-md border border-white/70 bg-white/10 flex items-center justify-center">
                      <div className="flex gap-0.5">
                        <span className="w-1.5 h-3 rounded-sm bg-emerald-200" />
                        <span className="w-1.5 h-3 rounded-sm bg-sky-200" />
                        <span className="w-1.5 h-3 rounded-sm bg-violet-200" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-1">
                      CORE INFRASTRUCTURE
                    </p>
                    <p className="text-lg font-semibold text-gray-900">
                      GPU 集群算力底座
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      多节点 GPU 服务器，代表性配置 A100 &amp; RTX 3090 集群
                    </p>
                  </div>
                </div>

                {/* 指标小面板 */}
                <div className="grid grid-cols-2 gap-3 text-xs md:text-sm">
                  <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2">
                    <p className="text-slate-500 mb-1">单节点显存上限</p>
                    <p className="font-semibold text-gray-900">320 GB +</p>
                  </div>
                  <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2">
                    <p className="text-slate-500 mb-1">单节点内存</p>
                    <p className="font-semibold text-gray-900">1 TB RAM</p>
                  </div>
                  <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2">
                    <p className="text-slate-500 mb-1">典型任务</p>
                    <p className="font-semibold text-gray-900">大模型 / 多组学</p>
                  </div>
                  <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2">
                    <p className="text-slate-500 mb-1">应用场景</p>
                    <p className="font-semibold text-gray-900">科研 &amp; 临床转化</p>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between text-[11px] text-slate-500">
                  <span>Multi-node • Multi-GPU • Production-ready</span>
                  <span className="rounded-full border border-slate-200 px-2 py-0.5 bg-slate-50">
                    AI &amp; Omics
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 下半部分：代表性集群配置表 */}
        <div className="overflow-x-auto bg-white border border-slate-200 rounded-2xl shadow-md">
          <table className="min-w-full text-xs md:text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-700">
                <th className="px-4 py-3 text-left font-semibold">集群名称</th>
                <th className="px-4 py-3 text-left font-semibold">GPU 配置</th>
                <th className="px-4 py-3 text-left font-semibold">GPU 总显存</th>
                <th className="px-4 py-3 text-left font-semibold">CPU 配置</th>
                <th className="px-4 py-3 text-left font-semibold">内存配置</th>
                <th className="px-4 py-3 text-left font-semibold">主要用途</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <tr>
                <td className="px-4 py-4 font-medium text-gray-900 whitespace-nowrap">
                  A100 计算集群
                </td>
                <td className="px-4 py-4 text-gray-800 whitespace-nowrap">
                  NVIDIA A100 80GB × 4
                </td>
                <td className="px-4 py-4 text-gray-800 whitespace-nowrap">
                  320 GB HBM2e
                </td>
                <td className="px-4 py-4 text-gray-800 whitespace-nowrap">
                  104 核心
                </td>
                <td className="px-4 py-4 text-gray-800 whitespace-nowrap">
                  1 TB RAM
                </td>
                <td className="px-4 py-4 text-gray-700">
                  大模型训练、模型微调、超大规模单细胞/多组学分析
                </td>
              </tr>
              <tr className="bg-slate-50/60">
                <td className="px-4 py-4 font-medium text-gray-900 whitespace-nowrap">
                  3090 开发集群
                </td>
                <td className="px-4 py-4 text-gray-800 whitespace-nowrap">
                  RTX 3090 24GB × 4
                </td>
                <td className="px-4 py-4 text-gray-800 whitespace-nowrap">
                  96 GB GDDR6X
                </td>
                <td className="px-4 py-4 text-gray-800 whitespace-nowrap">
                  96 核心
                </td>
                <td className="px-4 py-4 text-gray-800 whitespace-nowrap">
                  1 TB RAM
                </td>
                <td className="px-4 py-4 text-gray-700">
                  算法开发、模型调参、中大规模单细胞/多组学分析
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="mt-4 text-[11px] md:text-xs text-gray-500">
          注：除下列表所示代表性集群外，平台还包含多台不同规格的 GPU 与计算节点，可根据具体项目需求灵活扩展与调度。
        </p>
      </div>
    </section>
  );
};

export default ComputeClusterSection;
